import type { GetServerSidePropsContext, Redirect } from 'next';
import type { ServerResponse } from 'http';
import { ReactElement } from 'react';
import { MerchantDetail } from '../interfaces/merchants';

let als: typeof import('node:async_hooks');
if (typeof window === 'undefined') als = require('node:async_hooks');

// SSR, CSR いずれでも使える `window.location.href` の代替 `getRequestUrl` を提供するモジュール
// SSR 環境では *すべてのページで共通して使用している* injectAppContextGetServerSideProps を通して注入される
// FIXME: Pomelo: これモジュール実装確認時点では全ページで使われてることを確認したが、全ページで inject... を
//                挟むというお約束はやめたい...

let requestUrlStorage: unknown;

const getRequestUrlStorage = () => {
  type Payload = { requestUrl: string };
  requestUrlStorage ||= new als.AsyncLocalStorage<Payload>();
  return requestUrlStorage as import('node:async_hooks').AsyncLocalStorage<Payload>;
};

/**
 * Server Side の React Component レンダリングの文脈で getRequestUrl を使用できるように render する
 * @param render render処理本体
 */
export const reactElementWithRequestUrl = (
  requestUrl: string | undefined,
  render: () => ReactElement,
): ReactElement => {
  // CSRでは何もしない
  if (typeof window !== 'undefined' || !requestUrl) return render();

  return reactElementHook.wrapRender(requestUrl, render)();
};

const reactElementHook = {
  // render関数が呼ばれたときに requestUrl が乗るようにrender関数をwrapする
  wrapRender: <Args extends unknown[], Ret>(
    requestUrl: string,
    render: (...args: Args) => Ret,
  ): ((...args: Args) => Ret) => {
    return (...args: Args) => {
      return getRequestUrlStorage().run(
        { requestUrl },
        (...args: Args) =>
          reactElementHook.wrapRenderResult(requestUrl, render(...args)),
        ...args,
      );
      // XXX: 単に以下のようにはしない:
      // return getRequestUrlStorage().run({ requestUrl }, render, ...args)
      // 大変しんどいが、なぜか。
      // renderされた結果に含まれる ReactElement それぞれは、後続の手続きでまた render されていくが、
      // そのときはこの `run` のコンテキストの外で走ってしまう。そのため、それぞれの render が
      // 改めて reactElementHook.wrapRender されるように再帰的に wrap する必要がある
    };
  },
  // render結果 (T) に requestUrl が乗るようにwrapする
  wrapRenderResult: <T>(requestUrl: string, el: T): T => {
    // render結果が Array なら、
    if (Array.isArray(el)) {
      // それぞれの要素をwrap
      return el.map((c) =>
        reactElementHook.wrapRenderResult(requestUrl, c),
      ) as T;
    }

    // render結果が ReactElement なら、
    if (
      el &&
      typeof el === 'object' &&
      'type' in el &&
      'props' in el &&
      el.props &&
      typeof el.props === 'object'
    ) {
      let { type, props } = el; // satisfies ReactElement;

      // 1. Function Componentの場合、typeそのものがrender関数なので、それを reactElementHook.wrapRender
      //    NOTE: 関数の実体が変わるが、SSRで1度きりなのと、型情報は既に el.$$typeof に入っているので許容
      if (typeof type === 'function' && !type.prototype) {
        type = reactElementHook.wrapRender(requestUrl, type as () => unknown);
      }

      // 2. props.children には子のrender結果が入っているので、再帰的にwrapRenderResultを適用する
      //    NOTE: fallback={<Component />} のような他のpropsのElementも辿るべきだが、複雑すぎるのでスコープアウト
      if ('children' in props) {
        props = {
          ...props,
          children: reactElementHook.wrapRenderResult(
            requestUrl,
            props.children,
          ),
        };
      }

      Object.freeze(props);
      return Object.freeze({ ...el, type, props }); // satisfies ReactElement;
    }
    return el;
  },
};

/**
 * Server Side Fetchの文脈で getRequestUrl が使えるようにコンテキストを持った状態でbodyを実行する
 * (node API の AsyncLocalStorage を使用しているので、 AsyncLocalStorage の対応する非同期チェーンの範囲で動作)
 */
export const withRequestUrlFromServerSidePropsContext = async <T>(
  ctx: GetServerSidePropsContext,
  body: () => Promise<T>,
) => {
  // NOTE: forwardingの途中でpathname書き換えられてると成り立たないが、経路上ないはず
  const host = ctx.req.headers['x-forwarded-host'] || ctx.req.headers.host;
  const pathname = ctx.resolvedUrl;

  if (typeof host === 'string' && typeof pathname === 'string') {
    return getRequestUrlStorage().run(
      { requestUrl: `https://${host}${pathname}` },
      async () => await body(),
    );
  } else {
    return await body();
  }
};

const LEGACY_COUBIC_URL =
  process.env.NEXT_PUBLIC_LEGACY_COUBIC_URL || 'https://coubic.com';

export const getLegacyHost = () => new URL(LEGACY_COUBIC_URL).hostname;

export const getRequestHost = () => new URL(getRequestUrl()).hostname;

export const getRequestUrl = () => {
  // NOTE: このモジュールは全体影響するので、安全に倒して ... || 'https://coubic.com' している
  // 新ドメイン移行が終わるといずれにせよこの実装が正しく動かないと機能しなくなるので、そのタイミングでは
  // 以下のコメントアウトしてる形式にしてよい
  // return unsafeGloballyStashedRequestUrl || window.location.href
  return (
    (typeof window !== 'undefined'
      ? window.location.href
      : getRequestUrlStorage().getStore()?.requestUrl) || 'https://coubic.com'
  );
};

const NON_SITE_PAGES = ['inquiry', 'login', 'preparing', 'reserve'];

/**
 * 現在のパスから site_public_id を取得する
 */
export const getRequestSitePublicId = () => {
  const url = new URL(getRequestUrl());

  let path = url.pathname;
  if (url.hostname != getLegacyHost()) path = removeReservePrefix(path);

  const match = path.match(/^\/([^\/]+)/);
  if (!match) return null;

  const maySitePublicId = match[1];
  if (NON_SITE_PAGES.includes(maySitePublicId)) return null;

  return maySitePublicId;
};

/**
 * 以下の仕事を行い、パスから適切なURLを構築する:
 * * coubic.com にいるかどうかを判断し、 /reserve prefix を消すか付与する。
 *   mode=next のときは next/router や next/link のためのパスを生成するとして /reserve prefix を常に消す
 *   mode=legacy のときはリクエスト先は常に coubic.com とするとして /reserve prefix を常に消す
 * * mode=absolute のとき、現在のホストに基づいて絶対URLとして補完する
 *   mode=legacy のとき、リクエスト先は coubic.com とするとして絶対URLとして補完する
 *     !!!このとき代わりにヘッダの X-Coubic-Request-Host に getRequestHost() を指定すること!!!
 */
export const buildUrl = (
  pagePath: string,
  mode?: 'absolute' | 'next' | 'legacy' | false,
) => {
  if (pagePath.startsWith('https:')) return pagePath;

  if (
    mode == 'next' ||
    mode == 'legacy' ||
    getRequestHost() == getLegacyHost()
  ) {
    // coubic.com で動いているとき or next/link や next/router を使用するときは /reserve prefix は不要
    // NOTE: こっちパターンで渡す処理は無くしたほうがいい
    pagePath = removeReservePrefix(pagePath);
  } else {
    // coubic.com で動いていないときは /reserve prefix を補完
    pagePath = putReservePrefix(pagePath);
  }

  if (mode == 'absolute') return `https://${getRequestHost()}${pagePath}`;
  if (mode == 'legacy') return `https://${getLegacyHost()}${pagePath}`;
  return pagePath;
};

/**
 * /api/v2/... は頻出なのでヘルパー
 * Server Sideでは mode=legacy がデフォルトとなる
 *   !!!このとき代わりにヘッダの X-Coubic-Request-Host に getRequestHost() を指定すること!!!
 */
export const buildApiV2Url = (path: string, mode?: 'absolute' | 'legacy') => {
  if (path.startsWith('https:')) return path;

  mode ??= typeof window === 'undefined' ? 'legacy' : 'absolute';
  return buildUrl(`/api/v2${path}`, mode);
};

/**
 * /{site_public_id}/... は頻出なのでヘルパー
 * page なので, SSR でも mode は 'absolute' とはならない
 */
export const buildSiteUrl = (path = '', mode?: 'next' | false) => {
  if (path.startsWith('https:')) return path;

  return buildUrl(`/${getRequestSitePublicId()}${path}`, mode);
};

/**
 * 店舗詳細上のホスト情報と、現在のホストが一致しない場合、リダイレクト(getServerSidePropsの応答型)を返す
 */
export const redirectIfHostChanged = (
  merchantDetail?: MerchantDetail,
  res?: ServerResponse,
): { redirect: Redirect } | null => {
  if (!merchantDetail) return null;

  const requestURL = new URL(getRequestUrl());
  const appropriateHost = merchantDetail.enforce_divided_host
    ? merchantDetail.host
    : getLegacyHost();
  if (!appropriateHost || requestURL.hostname == appropriateHost) return null;

  const { site_public_id, public_id } = merchantDetail;
  let path = removeReservePrefix(requestURL.pathname); // 一度一律に /reserve prefix を消す

  path =
    appropriateHost == getLegacyHost()
      ? // from divided host to coubic.com
        replacePrefix(path, String(site_public_id), public_id)
      : // from coubic.com to divided host
        putReservePrefix(
          replacePrefix(path, public_id, String(site_public_id)),
        );

  const destination = `https://${appropriateHost}${path}${requestURL.search}`;
  const isPermanent = appropriateHost !== getLegacyHost();

  // coubic.comからのリダイレクト時のみCache-Controlを設定
  if (res && requestURL.hostname === getLegacyHost()) {
    res.setHeader('Cache-Control', 'no-store, must-revalidate');
  }

  return {
    redirect: {
      destination,
      permanent: isPermanent,
    },
  };
};

const putReservePrefix = (path: string) => {
  // /reservefoo みたいな文字列は /reserve/reservefoo になる
  if (path == '/reserve' || path.startsWith('/reserve/')) return path;
  return '/reserve' + path;
};

const removeReservePrefix = (path: string) => {
  if (path == '/reserve') return '';
  return path.replace(/^\/reserve\//, '/');
};

const replacePrefix = (path: string, from: string, to: string) => {
  if (path == `/${from}`) return `/${to}`;
  if (path.startsWith(`/${from}/`))
    return `/${to}/${path.slice(2 + from.length)}`;
  return path;
};
